import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import { Customer } from "../../types/customer";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddCustomerMutation,
  useEditCustomerMutation,
  useGetCustomerByIdQuery,
} from "../../services/customer.service";
import { useTitle } from "../../hooks/useTitle";
import { Controller, useForm } from "react-hook-form";
import useScroll from "../../hooks/useScroll";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import SelectCountry from "../../components/SelectCountry";
import SelectMapAddress from "../../components/SelectMapAddress";
import { AddressComponent } from "../../types/common";
import { useGetCountriesQuery } from "../../services/country.service";
import CoordinateInfo from "../../components/CoordinateInfo";
import { useUILabel } from "../../hooks/useUILabel";

const ClientEditPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [showAddress, setShowAddress] = React.useState(false);

  const [scrollToBasic, basicRef] = useScroll();
  const [scrollToAddress, addressRef] = useScroll();
  const [scrollToSocial, socialRef] = useScroll();

  const { data, isLoading } = useGetCustomerByIdQuery(parseInt(customerId!), {
    skip: !customerId,
  });
  const [add, { isLoading: adding }] = useAddCustomerMutation();
  const [edit, { isLoading: updating }] = useEditCustomerMutation();
  const { data: countries } = useGetCountriesQuery();

  const editClientLabel = useUILabel("client.editClient");
  const createClientLabel = useUILabel("client.createClient");
  const clientNameLabel = useUILabel("client.clientName");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const addressLabel = useUILabel("common.address");
  const createLabel = useUILabel("common.create");
  const updateLabel = useUILabel("common.update");
  const clientCreatedLabel = useUILabel("client.clientCreated");
  const clientUpdatedLabel = useUILabel("client.clientUpdated");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const zipLabel = useUILabel("common.zip");
  const address1Label = useUILabel("client.address1");
  const address2Label = useUILabel("client.address2");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");
  const generalLabel = useUILabel("client.general");
  const socialMediasLabel = useUILabel("client.socialMedias");
  const searchNewAddressLabel = useUILabel("client.searchNewAddress");
  const linkedInURLLabel = useUILabel("client.linkedInURL");
  const facebookURLLabel = useUILabel("client.facebookURL");
  const instagramURLLabel = useUILabel("client.instagramURL");
  const websiteURLLabel = useUILabel("client.websiteURL");
  useTitle(!!data ? editClientLabel : createClientLabel);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) {
        const customer: Customer = await add(payload).unwrap();
        toast.success(clientCreatedLabel);
        navigate(`/clients/${customer.id}`, { replace: true });
      } else {
        payload.id = data.id;
        await edit(payload).unwrap();
        toast.success(clientUpdatedLabel);
        navigate(`/clients/${data.id}`, { replace: true });
      }
    } catch (err) { }
  };

  const handleMapAddressChange = (
    address: AddressComponent,
    lat?: number,
    lng?: number
  ) => {
    setValue(
      "physical_address_1",
      `${address.street} ${address.street_number}`
    );
    setValue("city", address.city);
    setValue("state", address.state);
    setValue("zip", address.postal_code);
    setValue("lat", lat);
    setValue("lng", lng);
    const found = countries?.find(
      (it) => it.code.toLowerCase() == address.country.toLowerCase()
    );
    setValue("country", found?.id);
    setShowAddress(true);
  };

  React.useEffect(() => {
    setValue("first_name", data?.first_name || "");
    setValue("phone_number", data?.phone_number || "");
    setValue("email", data?.email || "");
    setValue("physical_address_1", data?.physical_address_1 || "");
    setValue("physical_address_2", data?.physical_address_2 || "");
    setValue("country", data?.country_id || 0);
    setValue("city", data?.city || "");
    setValue("state", data?.state || "");
    setValue("zip", data?.zip || "");
    setValue("linked_in", data?.linked_in || "");
    setValue("facebook", data?.facebook || "");
    setValue("instagram", data?.instagram || "");
    setValue("website", data?.website || "");

    const [lng, lat] = data?.coordinate?.coordinates || [];
    setValue("lat", lat || 0);
    setValue("lng", lng || 0);
    setShowAddress(!!lat && !!lng);
  }, [data]);

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <List>
            <ListItem>
              <ListItemButton onClick={scrollToBasic as any}>
                <ListItemText>{generalLabel}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton onClick={scrollToAddress as any}>
                <ListItemText>{addressLabel}</ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton onClick={scrollToSocial as any}>
                <ListItemText>{socialMediasLabel}</ListItemText>
              </ListItemButton>
            </ListItem>
          </List>
        </Grid>
        <Grid item xs={9}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <Card sx={{ mt: 1 }} ref={basicRef}>
                <CardHeader title={generalLabel} />
                <CardContent>
                  <Controller
                    name="first_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label={clientNameLabel}
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!errors.first_name}
                        helperText={
                          !!errors.first_name && thisFieldIsRequiredLabel
                        }
                      />
                    )}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Controller
                      name="phone_number"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label={phoneNumberLabel}
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          sx={{ mt: 0 }}
                          error={!!errors.phone_number}
                          helperText={
                            !!errors.phone_number && thisFieldIsRequiredLabel
                          }
                        />
                      )}
                    />
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <TextField
                          InputLabelProps={{ shrink: !!value }}
                          label={emailLabel}
                          value={value}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          sx={{ mt: 0 }}
                          error={!!errors.email}
                          helperText={
                            !!errors.email && thisFieldIsRequiredLabel
                          }
                        />
                      )}
                    />
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={addressRef}>
                <CardHeader title={addressLabel} />
                <CardContent>
                  {!showAddress ? (
                    <SelectMapAddress onChange={handleMapAddressChange} />
                  ) : (
                    <Box>
                      <Button onClick={() => setShowAddress(false)}>
                        {searchNewAddressLabel}
                      </Button>
                      <Controller
                        name="physical_address_1"
                        control={control}
                        rules={{ required: true }}
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <TextField
                            InputLabelProps={{ shrink: !!value }}
                            label={address1Label}
                            value={value}
                            fullWidth
                            onChange={onChange}
                            onBlur={onBlur}
                            error={!!errors.physical_address_1}
                            helperText={
                              !!errors.physical_address_1 &&
                              thisFieldIsRequiredLabel
                            }
                          />
                        )}
                      />
                      <Controller
                        name="physical_address_2"
                        control={control}
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <TextField
                            InputLabelProps={{ shrink: !!value }}
                            label={address2Label}
                            value={value}
                            fullWidth
                            onChange={onChange}
                            onBlur={onBlur}
                            sx={{ mt: 2 }}
                          />
                        )}
                      />
                      <Stack direction={"row"} spacing={1} sx={{ mt: 2 }}>
                        <Controller
                          name="city"
                          control={control}
                          rules={{ required: true }}
                          render={({
                            field: { onChange, onBlur, value, ref },
                          }) => (
                            <TextField
                              InputLabelProps={{ shrink: !!value }}
                              label={cityLabel}
                              value={value}
                              fullWidth
                              onChange={onChange}
                              onBlur={onBlur}
                              sx={{ mt: 0 }}
                              error={!!errors.city}
                              helperText={
                                !!errors.city && thisFieldIsRequiredLabel
                              }
                            />
                          )}
                        />
                        <Controller
                          name="state"
                          control={control}
                          rules={{ required: true }}
                          render={({
                            field: { onChange, onBlur, value, ref },
                          }) => (
                            <TextField
                              InputLabelProps={{ shrink: !!value }}
                              label={stateLabel}
                              value={value}
                              fullWidth
                              onChange={onChange}
                              onBlur={onBlur}
                              error={!!errors.state}
                              helperText={
                                !!errors.state && thisFieldIsRequiredLabel
                              }
                            />
                          )}
                        />
                        <Controller
                          name="zip"
                          control={control}
                          rules={{ required: true }}
                          render={({
                            field: { onChange, onBlur, value, ref },
                          }) => (
                            <TextField
                              InputLabelProps={{ shrink: !!value }}
                              label={zipLabel}
                              value={value}
                              fullWidth
                              onChange={onChange}
                              onBlur={onBlur}
                              error={!!errors.zip}
                              helperText={
                                !!errors.zip && thisFieldIsRequiredLabel
                              }
                            />
                          )}
                        />
                      </Stack>
                      <Controller
                        name="country"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <SelectCountry
                            onChange={onChange}
                            value={value}
                            error={!!errors.country}
                            helperText={
                              !!errors.country && thisFieldIsRequiredLabel
                            }
                          />
                        )}
                      />
                      <CoordinateInfo lat={watch("lat")} lng={watch("lng")} />
                    </Box>
                  )}
                </CardContent>
              </Card>
              <Card sx={{ mt: 1 }} ref={socialRef}>
                <CardHeader title={socialMediasLabel} />
                <CardContent>
                  <Controller
                    name="linked_in"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label={linkedInURLLabel}
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="facebook"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label={facebookURLLabel}
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="instagram"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label={instagramURLLabel}
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <Controller
                    name="website"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label={websiteURLLabel}
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </CardContent>
              </Card>
              <Stack direction="row" justifyContent={"flex-end"} sx={{ mt: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  loading={adding || updating}
                // sx={{ width: "50%" }}
                >
                  {!!data ? updateLabel : createLabel}
                </LoadingButton>
              </Stack>
            </Box>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};
export default ClientEditPage;
